import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_NAME = { guide: "Guide", fantasy: "Fantasy", pro: "Pro" };
const MAX_POLLS = 15;

function usePaymentStatus(sessionId) {
  const { refreshUser } = useAuth();
  const [state, setState] = useState({ phase: sessionId ? "checking" : "missing" });
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < MAX_POLLS && !cancelled; i++) {
        try {
          const r = await axios.get(`${API}/payments/status/${sessionId}`);
          if (r.data.payment_status === "paid") {
            await refreshUser();
            if (!cancelled) setState({ phase: "paid", plan: r.data.plan });
            return;
          }
          if (r.data.status === "expired") { if (!cancelled) setState({ phase: "expired" }); return; }
        } catch { /* keep polling */ }
        await new Promise((res) => setTimeout(res, 2000));
      }
      if (!cancelled) setState({ phase: "timeout" });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
  return state;
}

const COPY = {
  checking: ["Confirming your payment…", "This only takes a few seconds."],
  expired: ["Checkout expired.", "No charge was made. You can try again any time."],
  timeout: ["Still processing.", "Your payment is taking longer than usual. Refresh this page in a minute."],
  missing: ["Nothing to confirm.", "We couldn't find a checkout session."],
  canceled: ["Checkout canceled.", "No charge was made. Your plan hasn't changed."],
};

export default function PaymentResult({ canceled = false }) {
  const sessionId = canceled ? null : new URLSearchParams(window.location.search).get("session_id");
  const status = usePaymentStatus(sessionId);
  const phase = canceled ? "canceled" : status.phase;
  const [title, body] = phase === "paid"
    ? [`You're on ${PLAN_NAME[status.plan] || "your new plan"}.`, "Everything in your plan is unlocked — here and in the iPhone app."]
    : COPY[phase];
  return (
    <main className="min-h-screen bg-[#F4F3EC] text-[#111] flex items-center justify-center p-6" data-testid="payment-result-page">
      <div className="w-full max-w-lg border-2 border-[#111] p-8">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold">guidedsport · Billing</div>
        <h1 className="mt-3 font-display text-3xl md:text-4xl font-extrabold leading-tight inline-flex items-center gap-3" data-testid="payment-result-title">
          {phase === "checking" && <Loader2 size={24} className="spin-slow" />}
          {title}
        </h1>
        <p className="mt-3 text-[#555]" data-testid="payment-result-body">{body}</p>
        <Link to="/tracker" className="btn-block focus-ring mt-8 inline-flex" data-testid="payment-result-continue">
          Go to the schedule
        </Link>
      </div>
    </main>
  );
}
